<template>
    <div class="control-group">
        <div class="button-group">
            <MenuBarBoldButton v-if="toolbarOptions.includes('bold')" :editor="editor" :disabled="disabled"/>
            <MenuBarItalicButton v-if="toolbarOptions.includes('italic')" :editor="editor" :disabled="disabled"/>
            <MenuBarUnderlineButton v-if="toolbarOptions.includes('underline')" :editor="editor" :disabled="disabled"/>
            <MenuBarStrikeButton v-if="toolbarOptions.includes('strike')" :editor="editor" :disabled="disabled"/>
            <MenuBarLinkButton v-if="toolbarOptions.includes('link')" :editor="editor" :disabled="disabled"/>
            <button
                v-if="toolbarOptions.includes('clear-marks')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                @click="editor.chain().focus().unsetAllMarks().run()">
                Clear marks
            </button>
            <button
                v-if="toolbarOptions.includes('clear-nodes')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                @click="editor.chain().focus().clearNodes().run()">
                Clear nodes
            </button>
            <button
                v-if="toolbarOptions.includes('paragraph')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('paragraph') }"
                @click="editor.chain().focus().setParagraph().run()">
                Paragraph
            </button>
            <button
                v-if="toolbarOptions.includes('heading-1')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()">
                H1
            </button>
            <button
                v-if="toolbarOptions.includes('heading-2')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
                H2
            </button>
            <button
                v-if="toolbarOptions.includes('heading-3')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
                H3
            </button>
            <button
                v-if="toolbarOptions.includes('heading-4')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 4 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 4 }).run()">
                H4
            </button>
            <button
                v-if="toolbarOptions.includes('heading-5')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 5 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 5 }).run()">
                H5
            </button>
            <button
                v-if="toolbarOptions.includes('heading-6')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('heading', { level: 6 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 6 }).run()">
                H6
            </button>
            <MenuBarNumberedListButton v-if="toolbarOptions.includes('ordered-list')" :editor="editor" :disabled="disabled"/>
            <MenuBarBulletedListButton v-if="toolbarOptions.includes('bullet-list')" :editor="editor" :disabled="disabled"/>
            <button
                v-if="toolbarOptions.includes('blockquote')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                :class="{ 'is-active': editor.isActive('blockquote') }"
                @click="editor.chain().focus().toggleBlockquote().run()">
                Blockquote
            </button>
            <button
                v-if="toolbarOptions.includes('horizontal-rule')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                @click="editor.chain().focus().setHorizontalRule().run()">
                Horizontal rule
            </button>
            <button
                v-if="toolbarOptions.includes('hard-break')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled"
                @click="editor.chain().focus().setHardBreak().run()">
                Hard break
            </button>
            <button
                v-if="toolbarOptions.includes('undo')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled || !editor.can().chain().focus().undo().run()"
                @click="editor.chain().focus().undo().run()">
                Undo
            </button>
            <button
                v-if="toolbarOptions.includes('redo')"
                class="tiptap-btn"
                type="button"
                :disabled="disabled || !editor.can().chain().focus().redo().run()"
                @click="editor.chain().focus().redo().run()">
                Redo
            </button>
            <MenuBarColorButton v-if="toolbarOptions.includes('color')" :editor="editor" :disabled="disabled"/>
            <MenuBarHighlightButton v-if="toolbarOptions.includes('highlight')" :editor="editor" :disabled="disabled"/>
            <MenuBarAlignmentButton v-if="hasAlignmentButtons" :editor="editor" :toolbar-options="toolbarOptions" :disabled="disabled"/>
            <MenuBarClearButton v-if="toolbarOptions.includes('clear')" :editor="editor" :disabled="disabled"/>
            <MenuBarSizeButton v-if="toolbarOptions.includes('size')" :editor="editor" :disabled="disabled"/>
        </div>
    </div>
</template>

<script>
import { Editor } from '@tiptap/vue-2';

import MenuBarBoldButton from '@/common/components/tiptap/buttons/MenuBarBoldButton.vue';
import MenuBarItalicButton from '@/common/components/tiptap/buttons/MenuBarItalicButton.vue';
import MenuBarUnderlineButton from '@/common/components/tiptap/buttons/MenuBarUnderlineButton.vue';
import MenuBarStrikeButton from '@/common/components/tiptap/buttons/MenuBarStrikeButton.vue';
import MenuBarLinkButton from '@/common/components/tiptap/buttons/MenuBarLinkButton.vue';
import MenuBarNumberedListButton from '@/common/components/tiptap/buttons/MenuBarNumberedListButton.vue';
import MenuBarBulletedListButton from '@/common/components/tiptap/buttons/MenuBarBulletedListButton.vue';
import MenuBarClearButton from '@/common/components/tiptap/buttons/MenuBarClearButton.vue';
import MenuBarSizeButton from '@/common/components/tiptap/buttons/MenuBarSizeButton.vue';
import MenuBarColorButton from '@/common/components/tiptap/buttons/MenuBarColorButton.vue';
import MenuBarHighlightButton from '@/common/components/tiptap/buttons/MenuBarHighlightButton.vue';
import MenuBarAlignmentButton from '@/common/components/tiptap/buttons/MenuBarAlignmentButton.vue';
import {TextEditorToolbarOptions} from '@/constants';

export default {
    name: 'MenuBar',
    props: {
        editor: {
            type: Editor,
            required: true
        },
        toolbarOptions: {
            type: Array,
            default: () => TextEditorToolbarOptions.default
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasAlignmentButtons() {
            return ['left', 'center', 'right', 'justify'].some(option => this.toolbarOptions.includes(option));
        }
    },
    components: {
        MenuBarBoldButton,
        MenuBarItalicButton,
        MenuBarUnderlineButton,
        MenuBarStrikeButton,
        MenuBarLinkButton,
        MenuBarNumberedListButton,
        MenuBarBulletedListButton,
        MenuBarClearButton,
        MenuBarSizeButton,
        MenuBarColorButton,
        MenuBarHighlightButton,
        MenuBarAlignmentButton
    }
};
</script>
